import { inject, Injectable } from '@angular/core';
import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { PHARMACY_SIMPLE_VIEW_MODEL_KEYS, PharmacySimpleViewModel } from '@services/models/pharmacy-view.model';
import { PharmaciesSearchService } from '@services/pharmacies.search.service';
import { Store } from '@services/store';
import { OpenSearchQuerySimpleResult } from '@shared/providers/opensearch-api';
import { intersection, sortBy } from 'lodash';
import { defer, firstValueFrom, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PharmaciesSimpleSearchStore extends Store<PharmacySimpleViewModel> {
  private readonly searchService = inject(PharmaciesSearchService);

  protected getDataSource(key: string): Observable<any> {
    return this.searchService.get<PharmacySimpleViewModel>('ncpdp', key, Array.from(PHARMACY_SIMPLE_VIEW_MODEL_KEYS));
  }

  getPharmacies(ncpdps: string[]): Observable<PharmacySimpleViewModel[]> {
    return defer(async () => {
      const fromStore = await this.getFromStore(ncpdps);
      if (fromStore) return sortBy(Object.values(fromStore), 'name');
      return await this.loadPharmacies(ncpdps);
    });
  }

  private async getFromStore(ncpdps: string[]) {
    const keys = await this.keys();
    const compare = intersection(ncpdps, keys);
    if (compare.length === ncpdps.length) {
      return await this.getMany(ncpdps);
    }
  }

  private async loadPharmacies(ncpdps: string[]) {
    const query: OpenSearchRequestFilter[] = [
      {
        field: 'ncpdp',
        operator: 'in',
        value: ncpdps,
      },
    ];
    const data = await firstValueFrom(
      this.searchService.searchData<PharmacySimpleViewModel>(
        query,
        { from: 0, size: 10000 },
        { name: 'asc' },
        Array.from(PHARMACY_SIMPLE_VIEW_MODEL_KEYS),
      ),
      { defaultValue: { items: [], total: 0 } as OpenSearchQuerySimpleResult<PharmacySimpleViewModel> },
    );
    await Promise.all(data.items.map(item => this.set(item.ncpdp, item)));
    return data.items;
  }
}
